// src/services/api.js
// Serviço de comunicação com o backend PHP
//
// A base é '/api', não o domínio da API. O rewrite do vercel.json (produção) e
// o src/setupProxy.js (dev) encaminham /api/* para https://api-email.ramcore.com.br.
// Com isso a chamada deixa de ser cross-origin: some o preflight, e quem fala
// com a hospedagem passa a ser a Vercel e não o computador do usuário — saem da
// frente antivírus corporativo, proxy e WAF que filtram por IP de origem.
//
// ARMADILHA: se REACT_APP_API_URL continuar definida nas variáveis de ambiente
// da Vercel, ela sobrescreve o '/api' e nada disso vale. Tem que apagar lá.
const API_BASE = process.env.REACT_APP_API_URL || '/api';

class ApiService {
  constructor() {
    this.baseUrl = API_BASE;
    this.token = localStorage.getItem('coremail-token') || null;
    this._activeRequests = new Map(); // id -> { endpoint, startTime }
    this._requestId = 0;
    this._listeners = new Set(); // callbacks for state changes
  }

  // ===== Request tracking =====
  onRequestChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _notify() {
    const active = Array.from(this._activeRequests.values());
    this._listeners.forEach(fn => fn(active));
  }

  _startRequest(endpoint) {
    const id = ++this._requestId;
    // Extract readable label from endpoint
    const label = this._labelFromEndpoint(endpoint);
    this._activeRequests.set(id, { label, endpoint, startTime: Date.now() });
    this._notify();
    return id;
  }

  _endRequest(id) {
    this._activeRequests.delete(id);
    this._notify();
  }

  _labelFromEndpoint(ep) {
    if (ep.includes('action=sync')) return 'Sincronizando emails';
    if (ep.includes('action=list') && ep.includes('route=emails')) return 'Carregando emails';
    if (ep.includes('action=view')) return 'Carregando email';
    if (ep.includes('action=counts')) return 'Atualizando contadores';
    if (ep.includes('action=send')) return 'Enviando email';
    if (ep.includes('action=flag') || ep.includes('action=bulk_flag')) return 'Atualizando flags';
    if (ep.includes('action=delete') || ep.includes('action=bulk_delete')) return 'Excluindo';
    if (ep.includes('action=move')) return 'Movendo email';
    if (ep.includes('route=contacts')) return 'Carregando contatos';
    if (ep.includes('route=dashboard')) return 'Carregando dashboard';
    if (ep.includes('route=settings')) return 'Configurações';
    if (ep.includes('route=accounts')) return 'Contas';
    if (ep.includes('route=tags')) return 'Tags';
    if (ep.includes('route=drafts')) return 'Rascunhos';
    if (ep.includes('action=cleanup')) return 'Limpeza';
    if (ep.includes('route=ai')) return 'Assistente IA';
    return 'Processando';
  }

  // ===== HTTP helpers =====
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...(this.token ? { 'Authorization': `Bearer ${this.token}` } : {}),
      ...options.headers,
    };

    const reqId = this._startRequest(endpoint);
    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Try to parse JSON, but handle cases where server returns non-JSON (e.g. PHP fatal error)
      const contentType = response.headers.get('content-type') || '';
      let data;
      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        // Try parsing as JSON anyway (some servers don't set content-type correctly)
        try {
          data = JSON.parse(text);
        } catch {
          // Server returned non-JSON (HTML error page, PHP fatal error, etc.)
          const errorMsg = text.length > 200 ? text.substring(0, 200) + '...' : text;
          throw new ApiError(`Erro do servidor: ${response.status} — ${errorMsg}`, response.status, null);
        }
      }

      if (!response.ok) {
        throw new ApiError(data.error || 'Erro na requisição', response.status, data);
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') throw error;
      if (error instanceof ApiError) throw error;
      // "Failed to fetch" sozinho não diz nada e sempre parece erro de CORS.
      // Deixar explícito que a requisição não chegou a ter resposta HTTP separa
      // "servidor fora do ar" de "alguém no meio do caminho bloqueando".
      throw new ApiError(
        `Não foi possível alcançar a API (${error.message}). ` +
        `A requisição não teve resposta HTTP — verifique conexão, antivírus ou proxy da rede.`,
        0,
        null
      );
    } finally {
      this._endRequest(reqId);
    }
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  post(endpoint, body, options = {}) {
    return this.request(endpoint, { method: 'POST', body: JSON.stringify(body), ...options });
  }

  put(endpoint, body) {
    return this.request(endpoint, { method: 'PUT', body: JSON.stringify(body) });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // ===== Token =====
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('coremail-token', token);
    } else {
      localStorage.removeItem('coremail-token');
    }
  }

  getToken() {
    return this.token;
  }

  isAuthenticated() {
    return !!this.token;
  }

  // ===== ACCOUNTS / CONTAS =====

  /**
   * Listar todas as contas de email do usuário
   */
  async getAccounts() {
    return this.get('/index.php?route=accounts&action=list');
  }

  /**
   * Criar nova conta de email
   * @param {Object} account - { name, email, imap_host, imap_port, smtp_host, smtp_port, password }
   */
  async createAccount(account) {
    return this.post('/index.php?route=accounts&action=create', account);
  }

  /**
   * Atualizar conta existente
   * @param {number} id - ID da conta
   * @param {Object} account - campos a atualizar
   */
  async updateAccount(id, account) {
    return this.put(`/index.php?route=accounts&action=update&id=${id}`, account);
  }

  /**
   * Deletar conta
   * @param {number} id - ID da conta
   */
  async deleteAccount(id) {
    return this.delete(`/index.php?route=accounts&action=delete&id=${id}`);
  }

  /**
   * Testar conexão IMAP/SMTP de uma conta
   * @param {Object} account - dados da conta para testar
   */
  async testAccount(account) {
    return this.post('/index.php?route=accounts&action=test', account);
  }

  // ===== EMAILS =====

  /**
   * Listar emails de uma pasta
   * @param {string} folder - inbox, sent, drafts, trash, spam, archive, starred
   * @param {number} page - página (default 1)
   * @param {number} limit - emails por página (default 20)
   * @param {number|null} accountId - ID da conta (null = conta padrão)
   */
  async getEmails(folder = 'inbox', page = 1, limit = 20, accountId = null, signal = null, unreadOnly = false) {
    let url = `/index.php?route=emails&action=list&folder=${folder}&page=${page}&limit=${limit}`;
    if (accountId) url += `&account_id=${accountId}`;
    if (unreadOnly) url += `&unread_only=1`;
    return this.get(url, signal ? { signal } : {});
  }

  /**
   * Buscar emails com filtros (server-side)
   */
  async searchEmails(query = '', folder = '', dateFrom = '', dateTo = '', page = 1, limit = 50, accountId = null) {
    let url = `/index.php?route=emails&action=search&q=${encodeURIComponent(query)}&folder=${folder}&page=${page}&limit=${limit}`;
    if (dateFrom) url += `&date_from=${dateFrom}`;
    if (dateTo) url += `&date_to=${dateTo}`;
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  /**
   * Marcar múltiplos emails de uma vez
   */
  async bulkFlag(uids, flag, folder, accountId = null) {
    return this.post('/index.php?route=emails&action=bulk_flag', {
      uids, flag, folder, account_id: accountId,
    });
  }

  /**
   * Buscar todos os UIDs de uma pasta (leve, sem dados do email)
   */
  async getBulkUids(folder, accountId = null) {
    let url = `/index.php?route=emails&action=bulk_uids&folder=${folder}`;
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  /**
   * Excluir múltiplos emails de uma vez
   */
  async bulkDelete(uids, folder, permanent = false, accountId = null) {
    return this.post('/index.php?route=emails&action=bulk_delete', {
      uids, folder, permanent, account_id: accountId,
    });
  }

  /**
   * Sincronizar emails com o IMAP (busca apenas novos)
   */
  async syncEmails(folder = 'inbox', accountId = null, batch = 0, signal = null) {
    let url = `/index.php?route=emails&action=sync&folder=${folder}`;
    if (accountId) url += `&account_id=${accountId}`;
    if (batch > 0) url += `&batch=${batch}`;
    return this.get(url, signal ? { signal } : {});
  }

  async syncStatus(folder = 'inbox', accountId = null) {
    let url = `/index.php?route=emails&action=sync_status&folder=${folder}`;
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  /**
   * Buscar contadores de não lidos por pasta (do banco, rápido)
   */
  async getEmailCounts(accountId = null, signal = null) {
    let url = '/index.php?route=emails&action=counts';
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url, signal ? { signal } : {});
  }

  /**
   * Quick IMAP check for unread counts across ALL accounts
   * Connects to each account's IMAP, counts UNSEEN, updates flags
   */
  async quickUnreads() {
    return this.get('/index.php?route=emails&action=quick_unreads');
  }

  /**
   * Buscar corpo completo de um email
   * @param {number} uid - UID IMAP do email
   * @param {string} folder - pasta onde o email está
   * @param {number|null} accountId
   */
  async getEmailBody(uid, folder = 'inbox', accountId = null) {
    let url = `/index.php?route=emails&action=view&uid=${uid}&folder=${folder}`;
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  /**
   * Marcar email (lido/não lido/favorito)
   * @param {number} uid
   * @param {string} flag - 'read', 'unread', 'starred', 'unstarred'
   * @param {string} folder
   * @param {number|null} accountId
   */
  async flagEmail(uid, flag, folder = 'inbox', accountId = null) {
    return this.post('/index.php?route=emails&action=flag', {
      uid, flag, folder, account_id: accountId,
    });
  }

  /**
   * Mover email para outra pasta
   * @param {number} uid
   * @param {string} fromFolder
   * @param {string} toFolder
   * @param {number|null} accountId
   */
  async moveEmail(uid, fromFolder, toFolder, accountId = null) {
    return this.post('/index.php?route=emails&action=move', {
      uid, from_folder: fromFolder, to_folder: toFolder, account_id: accountId,
    });
  }

  /**
   * Deletar email
   * @param {number} uid
   * @param {string} folder
   * @param {boolean} permanent
   * @param {number|null} accountId
   */
  async deleteEmail(uid, folder = 'inbox', permanent = false, accountId = null) {
    return this.post('/index.php?route=emails&action=delete', {
      uid, folder, permanent, account_id: accountId,
    });
  }

  /**
   * Enviar email via SMTP
   */
  async sendEmail(data, attachments = []) {
    if (attachments.length > 0) {
      // Convert files to base64 to avoid ModSecurity blocking (XML, etc.)
      const base64Attachments = await Promise.all(
        attachments.map(file => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve({
            name: file.name,
            mime: file.type || 'application/octet-stream',
            size: file.size,
            data: reader.result.split(',')[1], // base64 without prefix
          });
          reader.onerror = () => reject(new Error('Erro ao ler arquivo: ' + file.name));
          reader.readAsDataURL(file);
        }))
      );
      // Send as JSON with base64 attachments
      return this.post('/index.php?route=emails&action=send', {
        ...data,
        attachments_base64: base64Attachments,
      });
    }
    return this.post('/index.php?route=emails&action=send', data);
  }

  // ===== CONTACTS / CONTATOS =====

  async searchContacts(query = '', limit = 8) {
    return this.get(`/index.php?route=contacts&action=search&q=${encodeURIComponent(query)}&limit=${limit}`);
  }

  async listContacts(page = 1) {
    return this.get(`/index.php?route=contacts&action=list&page=${page}`);
  }

  async addContact(email, name = '') {
    return this.post('/index.php?route=contacts&action=add', { email, name });
  }

  async deleteContact(id) {
    return this.delete(`/index.php?route=contacts&action=delete&id=${id}`);
  }

  // ===== DRAFTS / RASCUNHOS =====

  async getDrafts(page = 1, limit = 30) {
    return this.get(`/index.php?route=drafts&action=list&page=${page}&limit=${limit}`);
  }

  async getDraft(id) {
    return this.get(`/index.php?route=drafts&action=get&id=${id}`);
  }

  async saveDraft(data) {
    return this.post('/index.php?route=drafts&action=save', data);
  }

  async deleteDraft(draftId) {
    return this.post('/index.php?route=drafts&action=delete', { draft_id: draftId });
  }

  async getDraftCounts() {
    return this.get('/index.php?route=drafts&action=counts');
  }

  // ===== TAGS =====

  async getTags(accountId = null) {
    let url = '/index.php?route=tags&action=list';
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  async createTag(name, color, accountId = null) {
    // color can be a colorId like 'blue' or a hex like '#FF5733'
    const isHex = color?.startsWith('#');
    return this.post('/index.php?route=tags&action=create', {
      name,
      ...(isHex ? { color } : { colorId: color }),
      account_id: accountId,
    });
  }

  async updateTag(id, name, color) {
    const isHex = color?.startsWith('#');
    return this.post('/index.php?route=tags&action=update', {
      id, name,
      ...(isHex ? { color } : { colorId: color }),
    });
  }

  async deleteTag(id) {
    return this.post('/index.php?route=tags&action=delete', { id });
  }

  async assignTag(tagId, emailCacheId) {
    return this.post('/index.php?route=tags&action=assign', { tag_id: tagId, email_cache_id: emailCacheId });
  }

  async unassignTag(tagId, emailCacheId) {
    return this.post('/index.php?route=tags&action=unassign', { tag_id: tagId, email_cache_id: emailCacheId });
  }

  async getTagEmails(tagId, page = 1) {
    return this.get(`/index.php?route=tags&action=emails&tag_id=${tagId}&page=${page}`);
  }

  // ===== TAG RULES (auto-tagging) =====

  async getTagRules(accountId = null) {
    let url = '/index.php?route=tags&action=rules_list';
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  async createTagRule(tagId, ruleType, ruleValue, accountId = 0) {
    return this.post('/index.php?route=tags&action=rules_create', {
      tag_id: tagId, rule_type: ruleType, rule_value: ruleValue, account_id: accountId,
    });
  }

  async deleteTagRule(ruleId) {
    return this.post('/index.php?route=tags&action=rules_delete', { rule_id: ruleId });
  }

  // ===== AI =====

  async aiProcess(data) {
    return this.post('/index.php?route=ai&action=process', data);
  }

  /**
   * Resumir múltiplos emails via IA
   * @param {Array} emails - [{ subject, from, body, date }]
   * @param {string} mode - 'recent' | 'unread'
   */
  async aiSummarizeBatch(emails, mode = 'recent', model = 'gpt-5-mini') {
    return this.post('/index.php?route=ai&action=summarize_batch', { emails, mode, model });
  }

  async getAiCredits() {
    return this.get('/index.php?route=ai&action=credits');
  }

  /**
   * Listar pastas IMAP reais do servidor
   * @param {number|null} accountId
   */
  async getImapFolders(accountId = null) {
    let url = '/index.php?route=emails&action=folders';
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  /**
   * Gerar URL de download direto de um anexo
   * @param {number} uid - UID do email
   * @param {string} part - número da parte MIME
   * @param {string} filename - nome do arquivo
   * @param {string} folder - pasta do email
   * @param {number|null} accountId
   */
  getAttachmentDownloadUrl(uid, part, filename, folder = 'inbox', accountId = null) {
    let url = `${this.baseUrl}/index.php?route=emails&action=download&uid=${uid}&part=${encodeURIComponent(part)}&filename=${encodeURIComponent(filename)}&folder=${folder}&mode=download`;
    if (accountId) url += `&account_id=${accountId}`;
    if (this.token) url += `&token=${encodeURIComponent(this.token)}`;
    return url;
  }

  /**
   * Buscar anexo como base64 para preview in-app
   * @param {number} uid
   * @param {string} part
   * @param {string} filename
   * @param {string} folder
   * @param {number|null} accountId
   */
  async getAttachmentPreview(uid, part, filename, folder = 'inbox', accountId = null) {
    let url = `/index.php?route=emails&action=download&uid=${uid}&part=${encodeURIComponent(part)}&filename=${encodeURIComponent(filename)}&folder=${folder}&mode=preview`;
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  // ===== SETTINGS / CONFIGURAÇÕES =====

  /**
   * Buscar todas as configurações do usuário
   */
  async getSettings(accountId = null) {
    let url = '/index.php?route=settings&action=get';
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  /**
   * Salvar configurações
   * @param {Object} settings - { notifications: true, autoRead: true, ... }
   */
  async saveSettings(settings) {
    return this.post('/index.php?route=settings&action=save', settings);
  }

  // ===== ACCOUNT GROUPS =====

  async getAccountGroups() {
    return this.get('/index.php?route=settings&action=get_groups');
  }

  async saveAccountGroups(groups) {
    return this.post('/index.php?route=settings&action=save_groups', { groups });
  }

  // ===== AUTH =====

  async login(email, password) {
    const data = await this.post('/index.php?route=auth&action=login', { email, password });
    if (data.token) {
      this.setToken(data.token);
      localStorage.setItem('coremail-user', JSON.stringify(data.user));
    }
    return data;
  }

  async validateToken() {
    if (!this.token) return null;
    try {
      const data = await this.get('/index.php?route=auth&action=validate');
      if (data.success) {
        localStorage.setItem('coremail-user', JSON.stringify(data.user));
        return data.user;
      }
      this.logout();
    } catch (err) {
      // Só desloga se o token foi de fato rejeitado (401/403).
      // Falha de rede (status 0) ou erro 5xx não pode apagar a sessão —
      // antes, uma oscilação de conexão no boot deslogava o usuário.
      if (err.status === 401 || err.status === 403) {
        this.logout();
      } else {
        return this.getStoredUser();
      }
    }
    return null;
  }

  logout() {
    this.setToken(null);
    localStorage.removeItem('coremail-user');
    localStorage.removeItem('coremail-active-account');
  }

  getStoredUser() {
    try { return JSON.parse(localStorage.getItem('coremail-user')); } catch { return null; }
  }

  // ===== ADMIN: USER MANAGEMENT =====

  async getUsers() {
    return this.get('/index.php?route=auth&action=users');
  }

  async createUser(user) {
    return this.post('/index.php?route=auth&action=create_user', user);
  }

  async updateUser(id, user) {
    return this.put(`/index.php?route=auth&action=update_user&id=${id}`, user);
  }

  async deleteUser(id) {
    return this.delete(`/index.php?route=auth&action=delete_user&id=${id}`);
  }

  // ===== COMPANIES =====
  async getCompanies() {
    return this.get('/index.php?route=auth&action=companies');
  }

  async createCompany(data) {
    return this.post('/index.php?route=auth&action=create_company', data);
  }

  async updateCompany(id, data) {
    return this.put(`/index.php?route=auth&action=update_company&id=${id}`, data);
  }

  async deleteCompany(id) {
    return this.delete(`/index.php?route=auth&action=delete_company&id=${id}`);
  }

  // ===== DASHBOARD =====
  async getDashboardStats() {
    return this.get('/index.php?route=dashboard&action=stats');
  }

  async getUserDashboardStats(userId) {
    return this.get(`/index.php?route=dashboard&action=user_stats&user_id=${userId}`);
  }

  // ===== CONTACTS (enhanced) =====
  async getContacts(page = 1, limit = 50, search = '', sort = 'times_contacted', order = 'DESC') {
    let url = `/index.php?route=contacts&action=list&page=${page}&limit=${limit}&sort=${sort}&order=${order}`;
    if (search) url += `&q=${encodeURIComponent(search)}`;
    return this.get(url);
  }

  async updateContact(id, data) {
    return this.put(`/index.php?route=contacts&action=update&id=${id}`, data);
  }

  // ===== SPAM RULES =====
  async getSpamRules() {
    return this.get('/index.php?route=emails&action=spam_rules');
  }

  async addSpamRule(ruleType, pattern) {
    return this.post('/index.php?route=emails&action=spam_rules', { rule_type: ruleType, pattern });
  }

  async deleteSpamRule(id) {
    return this.delete(`/index.php?route=emails&action=spam_rules&id=${id}`);
  }

  // ===== CLEANUP =====
  async getCleanupStats(accountId) {
    let url = '/index.php?route=emails&action=cleanup_stats';
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }

  async cleanupDelete(accountId, criteria, options = {}) {
    return this.post('/index.php?route=emails&action=cleanup_delete', {
      account_id: accountId,
      criteria,
      ...options,
    });
  }

  // ===== SPAM WHITELIST =====
  async addToWhitelist(email, domain) {
    return this.post('/index.php?route=emails&action=spam_whitelist', { email, domain });
  }

  async getWhitelist() {
    return this.get('/index.php?route=emails&action=spam_whitelist');
  }

  async removeFromWhitelist(id) {
    return this.delete(`/index.php?route=emails&action=spam_whitelist&id=${id}`);
  }

  // ===== DASHBOARD FILTERED =====
  async getDashboardTopByAccount(accountId) {
    let url = '/index.php?route=dashboard&action=top_by_account';
    if (accountId && accountId !== 'all') url += `&account_id=${accountId}`;
    return this.get(url);
  }

  async getDashboardVolumeByAccount(accountId) {
    let url = '/index.php?route=dashboard&action=volume_by_account';
    if (accountId && accountId !== 'all') url += `&account_id=${accountId}`;
    return this.get(url);
  }

  // ===== CONTACTS (with account filter) =====
  async getContactStats(accountId = null) {
    let url = '/index.php?route=contacts&action=stats';
    if (accountId) url += `&account_id=${accountId}`;
    return this.get(url);
  }
}

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

// Singleton
const api = new ApiService();
export default api;
export { ApiError };
